/**
 * Pilot Maps — HIPAA-Compliant Embeddable Map Widget
 * © Pilot Digital Marketing
 *
 * Entry point. Finds all [data-pilot-map] containers on the page,
 * reads their configuration, and initializes a map instance for each.
 *
 * v4: MapLibre GL JS with self-hosted vector tiles.
 */

import { loadMapLibre } from './loader.js';
import { fetchSheetData, fetchJsonData } from './data-loader.js';
import { createMap } from './map-builder.js';
import { addMarkers, buildPopupHTML } from './markers.js';
import { ClusterManager } from './cluster.js';
import { buildSidebar } from './sidebar.js';
import { buildFilters } from './filters.js';
import { applyA11y } from './a11y.js';
import { DEFAULTS } from './config.js';

/**
 * Parse all data-* attributes from a container element into a config object.
 */
function parseConfig(container) {
  const d = container.dataset;
  return {
    sheet: d.sheet || null,
    json: d.json || null,

    height: d.height || DEFAULTS.height,
    zoom: d.zoom ? parseInt(d.zoom, 10) : null,
    center: d.center ? d.center.split(',').map(Number) : null,
    style: d.style || DEFAULTS.style,
    minZoom: d.minZoom ? parseInt(d.minZoom, 10) : DEFAULTS.minZoom,
    maxZoom: d.maxZoom ? parseInt(d.maxZoom, 10) : DEFAULTS.maxZoom,

    sidebar: d.sidebar !== 'false',
    search: d.search === 'true',
    cluster: d.cluster === 'true',
    list: d.list === 'true',
    filters: d.filters ? d.filters.split(',').map(s => s.trim()) : [],

    popupFields: d.popupFields
      ? d.popupFields.split(',').map(s => s.trim())
      : DEFAULTS.popupFields,

    brandColor: d.brandColor || DEFAULTS.brandColor,
    directions: d.directions || DEFAULTS.directions,
    apiKey: d.apiKey || null,
    tileServer: d.tileServer || DEFAULTS.tileServer,
    scrollZoom: d.scrollZoom === 'true',
  };
}

const VALIDATION_ENDPOINT = 'https://maps.sounderdata.com/api/validate';

/**
 * Validate the API key against the Sounder validation service.
 * Returns true if valid or if no key is configured (backward compat).
 */
async function validateApiKey(apiKey) {
  if (!apiKey) return true; // No key set — allow load (backwards compat).

  const domain = window.location.hostname.replace(/^www\./, '');

  try {
    const response = await fetch(VALIDATION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: apiKey, domain }),
    });
    const data = await response.json();
    if (!data.valid) {
      console.error('[PilotMaps] API key validation failed:', data.error);
    }
    return data.valid === true;
  } catch (err) {
    // Network error — fail open so a transient outage doesn't take down client maps.
    console.warn('[PilotMaps] API validation unreachable, proceeding:', err.message);
    return true;
  }
}

/**
 * Initialize a single map instance inside a container.
 */
async function initMap(container) {
  const config = parseConfig(container);

  if (!config.sheet && !config.json) {
    console.error('[PilotMaps] No data source specified. Use data-sheet or data-json.');
    container.innerHTML = '<p style="padding:20px;color:#c0392b;">Map error: No data source configured.</p>';
    return;
  }

  if (!container.style.height && !container.classList.length) {
    container.style.height = config.height;
  }

  container.innerHTML = `<div class="pm-loading" role="status" aria-label="Loading map">
    <div class="pm-loading-spinner"></div>
    <span>Loading locations…</span>
  </div>`;

  try {
    // 1. Validate API key
    const isValid = await validateApiKey(config.apiKey);
    if (!isValid) {
      container.innerHTML = '<p style="padding:20px;color:#c0392b;">Map unavailable: invalid or unauthorized API key. Contact your administrator.</p>';
      return;
    }

    // 2. Load MapLibre GL JS
    await loadMapLibre();

    // 3. Fetch location data
    let locations;
    if (config.sheet) {
      locations = await fetchSheetData(config.sheet);
    } else {
      locations = await fetchJsonData(config.json);
    }

    if (!locations || locations.length === 0) {
      container.innerHTML = '<p style="padding:20px;color:#666;">No locations found.</p>';
      return;
    }

    // 4. Build DOM structure
    container.innerHTML = '';
    container.classList.add('pm-container');

    const showSidebar = config.sidebar && locations.length > 1;

    let mapEl, sidebarEl;

    if (showSidebar) {
      container.classList.add('pm-has-sidebar');

      sidebarEl = document.createElement('div');
      sidebarEl.className = 'pm-sidebar';
      sidebarEl.setAttribute('role', 'region');
      sidebarEl.setAttribute('aria-label', 'Location list');
      container.appendChild(sidebarEl);
    }

    const mapWrapper = document.createElement('div');
    mapWrapper.className = 'pm-map-wrapper';
    container.appendChild(mapWrapper);

    mapEl = document.createElement('div');
    mapEl.className = 'pm-map';
    mapEl.id = 'pm-map-' + Math.random().toString(36).substr(2, 6);
    mapWrapper.appendChild(mapEl);

    // 4. Initialize MapLibre map
    const map = createMap(mapEl, config, locations);

    // 5. Add markers or clusters (after map style loads)
    let markers = [];
    let clusterManager = null;

    await new Promise((resolve) => {
      const init = () => {
        if (config.cluster) {
          clusterManager = new ClusterManager(map, locations, config, buildPopupHTML);
          markers = clusterManager.getMarkerRefs();
        } else {
          const result = addMarkers(map, locations, config);
          markers = result.markers;
        }
        resolve();
      };

      if (map.isStyleLoaded()) {
        init();
      } else {
        map.on('load', init);
      }
    });

    // 6. Build sidebar if enabled
    if (showSidebar) {
      buildSidebar(sidebarEl, locations, markers, map, config, clusterManager);
    }

    // 7. Build filters if configured
    if (config.filters.length > 0 && showSidebar) {
      buildFilters(sidebarEl, locations, markers, map, config, clusterManager);
    }

    // 8. Build accessible text list if enabled
    if (config.list) {
      const listEl = document.createElement('div');
      listEl.className = 'pm-list-view';
      container.parentNode.insertBefore(listEl, container.nextSibling);
      buildAccessibleList(listEl, locations, markers, map, config);
    }

    // 9. Apply accessibility enhancements
    applyA11y(container, map, markers, config);

    // 10. Inject scoped styles
    injectStyles(config);

    // 11. Resize map after layout stabilizes
    requestAnimationFrame(() => {
      map.resize();
      if (!config.center || !config.zoom) {
        const maplibregl = window.maplibregl;
        const bounds = new maplibregl.LngLatBounds();
        locations.forEach(loc => bounds.extend([loc.lng, loc.lat]));
        map.fitBounds(bounds, { padding: 40, maxZoom: 15 });
      }
    });

  } catch (err) {
    console.error('[PilotMaps] Initialization failed:', err);
    container.innerHTML = '<p style="padding:20px;color:#c0392b;">Map failed to load. Please try again later.</p>';
  }
}

/**
 * Build an accessible HTML table of all locations below the map.
 */
function buildAccessibleList(el, locations, markers, map, config) {
  const table = document.createElement('table');
  table.className = 'pm-accessible-table';
  table.setAttribute('role', 'table');
  table.setAttribute('aria-label', 'Location directory');

  const displayKeys = ['name', 'address', 'phone', 'hours'].filter(k =>
    locations.some(loc => loc[k])
  );

  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  displayKeys.forEach(key => {
    const th = document.createElement('th');
    th.setAttribute('scope', 'col');
    th.textContent = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  locations.forEach((loc, i) => {
    const tr = document.createElement('tr');
    tr.style.cursor = 'pointer';
    tr.setAttribute('tabindex', '0');
    tr.setAttribute('aria-label', `View ${loc.name} on map`);

    const handleClick = () => {
      const m = markers[i];
      if (!m) return;

      const targetZoom = Math.max(map.getZoom(), 14);
      el.previousElementSibling?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      map.once('moveend', () => {
        m.marker.togglePopup();
      });

      map.flyTo({
        center: [loc.lng, loc.lat],
        zoom: targetZoom,
        duration: 800,
      });
    };

    tr.addEventListener('click', handleClick);
    tr.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    });

    displayKeys.forEach(key => {
      const td = document.createElement('td');
      if (key === 'phone' && loc[key]) {
        const link = document.createElement('a');
        link.href = 'tel:' + loc[key].replace(/[^\d+]/g, '');
        link.textContent = loc[key];
        link.addEventListener('click', e => e.stopPropagation());
        td.appendChild(link);
      } else if (key === 'name') {
        const span = document.createElement('span');
        span.textContent = loc[key] || '';
        span.style.cssText = `color:${config.brandColor};text-decoration:underline;`;
        td.appendChild(span);
      } else {
        td.textContent = loc[key] || '';
      }
      tr.appendChild(td);
    });

    tbody.appendChild(tr);
  });

  table.appendChild(tbody);
  el.appendChild(table);
}

/**
 * Inject scoped CSS styles.
 */
function injectStyles(config) {
  if (document.getElementById('pm-styles')) return;

  const style = document.createElement('style');
  style.id = 'pm-styles';
  style.textContent = getWidgetCSS(config.brandColor);
  document.head.appendChild(style);
}

/**
 * All widget CSS — scoped with pm- prefix.
 */
function getWidgetCSS(brandColor) {
  return `
/* ================================================
   Pilot Maps — Scoped Widget Styles (MapLibre v4)
   All classes prefixed with pm- to avoid conflicts
   ================================================ */

.pm-container {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 300px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

/* Loading state */
.pm-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  color: #666;
  font-size: 14px;
  gap: 12px;
}
.pm-loading-spinner {
  width: 28px; height: 28px;
  border: 3px solid #e0e0e0;
  border-top-color: ${brandColor};
  border-radius: 50%;
  animation: pm-spin 0.8s linear infinite;
}
@keyframes pm-spin { to { transform: rotate(360deg); } }

/* Layout */
.pm-map-wrapper {
  flex: 1;
  min-width: 0;
  position: relative;
}
.pm-map {
  width: 100%;
  height: 100%;
  min-height: 300px;
}
.pm-has-sidebar .pm-sidebar {
  width: 300px;
  flex-shrink: 0;
  overflow: hidden;
  border-right: 1px solid #e0e0e0;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.pm-sidebar-header {
  flex-shrink: 0;
}
.pm-sidebar-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

/* Sidebar */
.pm-sidebar-search {
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
}
.pm-sidebar-search input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  font-family: inherit;
}
.pm-sidebar-search input:focus {
  border-color: ${brandColor};
  box-shadow: 0 0 0 2px ${brandColor}33;
}

/* Filter section */
.pm-filters {
  padding: 10px 12px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}
.pm-filters-heading {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: ${brandColor};
  margin: 0 0 10px 0;
  padding: 0;
  cursor: pointer;
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pm-filters-heading:focus {
  outline: 2px solid ${brandColor};
  outline-offset: 2px;
  border-radius: 2px;
}
.pm-filters-arrow {
  font-size: 10px;
  display: inline-block;
  transition: transform 0.2s ease;
  margin-left: 6px;
}
.pm-filters.pm-filters-collapsed .pm-filters-arrow {
  transform: rotate(-90deg);
}
.pm-filters.pm-filters-collapsed .pm-filters-body {
  display: none;
}
.pm-filters.pm-filters-collapsed {
  padding-bottom: 10px;
}
.pm-filter-group {
  margin-bottom: 8px;
  padding-left: 5px;
}
.pm-filter-group:last-child {
  margin-bottom: 0;
}
.pm-filter-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
  margin-bottom: 4px;
}
.pm-filter-select {
  width: 100%;
  padding: 6px 28px 6px 8px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  background: #fff;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 11px;
}
.pm-filter-select:focus {
  border-color: ${brandColor};
  box-shadow: 0 0 0 2px ${brandColor}33;
  outline: none;
}

/* Location list items */
.pm-location-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.pm-location-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 13px;
}
.pm-location-item:hover,
.pm-location-item:focus-within {
  background: #f5f8ff;
}
.pm-location-item.pm-active {
  background: ${brandColor}11;
  border-left: 3px solid ${brandColor};
}
.pm-location-item[aria-hidden="true"] {
  display: none;
}
.pm-loc-name {
  font-weight: 600;
  color: #222;
  margin-bottom: 2px;
}
.pm-loc-address {
  color: #666;
  font-size: 12px;
  line-height: 1.4;
}
.pm-loc-meta {
  color: #888;
  font-size: 11px;
  margin-top: 2px;
}
.pm-container button.pm-location-btn {
  display: block !important;
  width: 100% !important;
  background: none !important;
  background-color: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  text-align: left !important;
  cursor: pointer !important;
  font-family: inherit !important;
  color: inherit !important;
  transition: none !important;
}
.pm-container .maplibregl-popup-close-button {
  background: none !important;
  background-color: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  color: #666 !important;
  padding: 2px 6px !important;
  font-size: 18px !important;
  transition: none !important;
}
.pm-container .maplibregl-popup-close-button:hover {
  color: #333 !important;
  background-color: rgba(0,0,0,0.05) !important;
}
.pm-location-btn:focus {
  outline: 2px solid ${brandColor};
  outline-offset: -2px;
  border-radius: 2px;
}

/* Popup */
.pm-popup h4 {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  margin: 0;
  line-height: 1.3;
}
.pm-popup p {
  font-size: 12px;
  color: #555;
  margin: 2px 0;
  line-height: 1.4;
}

/* Popup image */
.pm-popup-image {
  margin: -12px -16px 10px -16px;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
}
.pm-popup-image img {
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
}

/* Popup header (name + status) */
.pm-popup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

/* Open/Closed status badge */
.pm-status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}
.pm-status-open {
  background: #dcfce7;
  color: #166534;
}
.pm-status-closed {
  background: #fee2e2;
  color: #991b1b;
}
.pm-status-badge-sm {
  font-size: 9px;
  padding: 1px 6px;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

/* Accepting patients indicator */
.pm-accepting {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 0;
  margin-bottom: 4px;
}
.pm-accepting-icon {
  font-size: 12px;
  line-height: 1;
}
.pm-accepting-yes { color: #166534; }
.pm-accepting-no { color: #991b1b; }
.pm-accepting-call { color: #92400e; }

/* Sidebar accepting indicator */
.pm-loc-accepting {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  margin-top: 2px;
}
.pm-loc-accepting .pm-accepting-icon {
  font-size: 10px;
}

/* Popup body fields */
.pm-popup-body {
  margin-bottom: 8px;
}
.pm-popup-field {
  font-size: 12px;
  color: #555;
  margin: 3px 0;
  line-height: 1.4;
  position: relative;
}
/* Screen-reader-only text: visually hidden but announced */
.pm-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.pm-popup-field a {
  color: ${brandColor};
  text-decoration: none;
}
.pm-popup-hours {
  font-size: 11px;
  line-height: 1.6;
}
.pm-popup-field a:hover {
  text-decoration: underline;
}

/* Styled Get Directions link */
.pm-popup-directions-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  color: ${brandColor};
  text-decoration: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin-top: 6px;
  margin-bottom: 4px;
}
.pm-popup-directions-btn:hover {
  text-decoration: underline;
}
.pm-popup-directions-btn:focus {
  outline: 2px solid ${brandColor};
  outline-offset: 2px;
  border-radius: 2px;
}
.pm-directions-icon {
  display: flex;
  align-items: center;
}
.pm-directions-icon svg {
  stroke: ${brandColor};
}

/* Override MapLibre popup defaults for cleaner look */
.pm-maplibre-popup .maplibregl-popup-content {
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  padding: 12px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.pm-maplibre-popup .maplibregl-popup-tip {
  border-top-color: #fff;
}
.pm-maplibre-popup .maplibregl-popup-close-button {
  font-size: 18px;
  color: #666;
  padding: 4px 8px;
  right: 2px;
  top: 2px;
}
.pm-maplibre-popup .maplibregl-popup-close-button:hover {
  color: #222;
  background: transparent;
}

/* ---- Compact horizontal popup layout (for short maps <500px) ---- */
.pm-popup-compact .pm-popup-row {
  display: flex;
  gap: 12px;
  margin: -12px -16px 8px -16px;
  padding: 12px 16px 0 16px;
}
.pm-popup-compact .pm-popup-thumb {
  flex-shrink: 0;
  width: 110px;
  height: 100px;
  border-radius: 6px;
  overflow: hidden;
}
.pm-popup-compact .pm-popup-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.pm-popup-compact .pm-popup-content {
  flex: 1;
  min-width: 0;
}
.pm-popup-compact .pm-popup-header h4 {
  font-size: 13px;
}
.pm-popup-compact .pm-popup-field {
  font-size: 11px;
}
.pm-popup-compact .pm-accepting {
  font-size: 10px;
}
.pm-popup-compact .pm-popup-directions-btn {
  font-size: 11px;
}

/* Marker icon — remove MapLibre default marker styling */
.pm-marker-icon {
  background: none !important;
  border: none !important;
  cursor: pointer;
}
.pm-marker-icon:focus {
  outline: 2px solid ${brandColor};
  outline-offset: 2px;
  border-radius: 50%;
}

/* No results */
.pm-no-results {
  padding: 16px 12px;
  text-align: center;
  color: #888;
  font-size: 13px;
}

/* Accessible list view (below map) */
.pm-accessible-table {
  width: 100%;
  border-collapse: collapse;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  margin-top: 12px;
}
.pm-accessible-table th {
  text-align: left;
  padding: 8px 12px;
  background: #f5f5f5;
  border-bottom: 2px solid #ddd;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #666;
}
.pm-accessible-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}
.pm-accessible-table a {
  color: ${brandColor};
  text-decoration: none;
}
.pm-accessible-table a:hover { text-decoration: underline; }
.pm-accessible-table a:focus {
  outline: 2px solid ${brandColor};
  outline-offset: 2px;
}
.pm-accessible-table tbody tr:hover {
  background: ${brandColor}08;
}
.pm-accessible-table tbody tr:focus {
  outline: 2px solid ${brandColor};
  outline-offset: -2px;
  border-radius: 2px;
}

/* Mobile popup sizing */
@media (max-width: 640px) {
  .pm-popup-thumb {
    display: none !important;
  }
  .pm-popup-content {
    padding: 6px 8px !important;
  }
  .pm-popup-header h4 {
    font-size: 13px !important;
    margin: 0 0 4px 0 !important;
  }
  .pm-popup-field {
    font-size: 11px !important;
    margin: 2px 0 !important;
  }
  .pm-popup-directions-btn {
    font-size: 12px !important;
    padding: 4px 8px !important;
    margin-top: 4px !important;
  }
  .pm-status-badge {
    font-size: 10px !important;
    padding: 1px 5px !important;
  }
  .pm-accepting {
    font-size: 11px !important;
  }
  .pm-popup-hours,
  .pm-popup-services {
    display: none !important;
  }
}

/* Scroll fade on location list */
.pm-sidebar::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 32px;
  background: linear-gradient(transparent, #ffffff);
  pointer-events: none;
  z-index: 2;
}

/* Filters toggle — hidden on desktop, shown on mobile */
.pm-container button.pm-filters-toggle {
  display: none;
  width: 100% !important;
  padding: 8px 12px !important;
  background: #f5f5f5 !important;
  background-color: #f5f5f5 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #444 !important;
  cursor: pointer !important;
  text-align: left !important;
  margin-bottom: 4px !important;
  transition: none !important;
}
.pm-container button.pm-filters-toggle:hover {
  background: #eee !important;
  background-color: #eee !important;
}
.pm-filters-toggle-icon {
  display: inline-block;
  width: 14px;
}

/* On desktop, filters always visible */
.pm-filters-wrapper .pm-filters {
  display: block;
}

/* Responsive: stack sidebar on mobile */
@media (max-width: 640px) {
  .pm-container {
    flex-direction: column;
  }
  .pm-has-sidebar .pm-sidebar {
    width: 100%;
    max-height: 320px;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
  .pm-has-sidebar {
    height: auto !important;
    min-height: 700px;
  }
  .pm-has-sidebar .pm-map {
    min-height: 400px;
  }

  /* Show toggle, hide filters by default on mobile */
  .pm-container button.pm-filters-toggle {
    display: block;
  }
  .pm-filters-wrapper .pm-filters {
    display: none;
  }
  .pm-filters-wrapper.pm-filters-open .pm-filters {
    display: block;
  }
}
`;
}

/**
 * Auto-initialize all [data-pilot-map] elements on the page.
 */
function autoInit() {
  document.querySelectorAll('[data-pilot-map]').forEach(initMap);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

export { initMap, parseConfig };
